import {IFetchContext} from "@pg-mono/data-fetcher";
import {createRequestActionTypes} from "@pg-mono/request-state";
import {enable302ResponseState, enableErrorResponseState} from "@pg-mono/response-state";

import {IRPStore} from "../../../app/rp_reducer";
import {IRPRequestMeta} from "../../../app/rp_request_meta";
import {IAppDispatch} from "../../../store/store";
import {getRcnDataApi} from "../api/get_rcn_data_api";
import {getDefaultRoomsFilter, PRICES_ADDRESS_ROOMS} from "../constants/prices_address_rooms";
import {ALL_ROOMS, RoomsFilter} from "../constants/rcn_rooms_filter";
import {IPricesAddressRegion} from "../types/IPricesAddress";
import {RcnPriceStatsQuery} from "../types/RcnPriceStatsQuery";
import {RcnPriceStatsResponse} from "../types/RcnPriceStatsResponse";
import {getPublishableRcnPoint} from "../utils/get_publishable_rcn_point";
import {getRcnHistoryQuery, getRcnPeriodQuery} from "../utils/get_rcn_params";
import {fetchRcnLatestPoint, getRcnLatestYearQuery, IRcnResolvedPoint} from "../utils/resolve_rcn_latest_point";
import {getPricesAddressUnavailableUrl} from "./fetch_prices_address";

const FETCH_PRICES_ADDRESS_ROOMS = "reports/FETCH_PRICES_ADDRESS_ROOMS";
export const fetchPricesAddressRoomsTypes = createRequestActionTypes(FETCH_PRICES_ADDRESS_ROOMS);

const RCN_UNAVAILABLE_STATUS = 500;

// The aggregate first, then one probe per room count. The aggregate decides whether the page exists at
// all - it covers rooms 1-10 against the registry's three-transaction floor, so it publishes for addresses
// where no single size does. The per-size probes only fill the selector, and they double as a fallback:
// one flaky request must not turn an address that has data into a 500.
const PROBE_FILTERS: readonly RoomsFilter[] = [ALL_ROOMS, ...PRICES_ADDRESS_ROOMS];

interface IRcnResult {
    isSuccess: boolean;
    response: RcnPriceStatsResponse | null;
}

/**
 * 1. One probe per rooms filter: its newest YEAR point. A month or a quarter with three sales means its year has
 *    them too, so the year alone answers whether the filter publishes anything at all, however long ago.
 * 2. For every filter that does, the newest month and quarter in parallel - the year is the probe, a cache hit - and
 *    the latest period wins (fetchRcnLatestPoint, the rule the page hooks apply to the same three queries), so the
 *    headline, the rooms tabs and the spread hydrate without a request. History alongside: the year series (the
 *    grain useRcnHistory opens on) - the query useRcnHistory makes.
 * 3. For the opening filter: "total" and the district / city for the resolved period, in the resolved grain.
 *
 * No query depends on "today", and market is always omitted (both markets combined, one request per read - see
 * get_rcn_params), so the browser builds the same cache keys whenever the (cached) page is hydrated.
 */
export const fetchPricesAddressRcnData = (_ctx: IFetchContext<IRPRequestMeta>) => async (dispatch: IAppDispatch, getState: () => IRPStore) => {
    const address = getState().report.price.address;

    if (!address) {
        return false;
    }

    dispatch({type: fetchPricesAddressRoomsTypes.start});

    const fetchRcnResponse = (query: RcnPriceStatsQuery): Promise<RcnPriceStatsResponse> =>
        dispatch(getRcnDataApi.endpoints.getRcnData.initiate(query)).unwrap();
    const fetchRcn = (query: RcnPriceStatsQuery): Promise<IRcnResult> =>
        fetchRcnResponse(query)
            .then((response) => ({isSuccess: true, response}))
            .catch(() => ({isSuccess: false, response: null}));

    const probeResults = await Promise.all(PROBE_FILTERS.map((rooms) => fetchRcn(getRcnLatestYearQuery(address.rcnAddress, rooms))));

    // An empty series means the registry has no group of three transactions, while a rejected request
    // means the registry could not be reached - only the former is an answer about this address.
    if (probeResults.every((result) => !result.isSuccess)) {
        dispatch(enableErrorResponseState(RCN_UNAVAILABLE_STATUS));

        return false;
    }

    const availableRooms = PROBE_FILTERS.filter((_rooms, index) => getPublishableRcnPoint(probeResults[index].response) !== null);

    if (availableRooms.length === 0) {
        // "No data" is only an answer when every probe gave one: a failed probe - above all the aggregate's, which
        // publishes for buildings where no single size does - leaves the address unknown, not unavailable.
        if (probeResults.some((result) => !result.isSuccess)) {
            dispatch(enableErrorResponseState(RCN_UNAVAILABLE_STATUS));

            return false;
        }

        dispatch(enable302ResponseState(getPricesAddressUnavailableUrl()));

        return false;
    }

    dispatch({type: fetchPricesAddressRoomsTypes.success, result: availableRooms});

    const defaultRooms = getDefaultRoomsFilter(availableRooms);
    // A failed month or quarter leaves the newest point of the grains that answered (failedGrains) - the page shows
    // it, and the hooks retry the failed grain in the browser. The year itself cannot fail here: it is the probe.
    const resolveLatest = (rooms: RoomsFilter): Promise<IRcnResolvedPoint | null> =>
        fetchRcnLatestPoint(fetchRcnResponse, address.rcnAddress, rooms).then(
            ({resolved}) => resolved,
            () => null
        );

    const [resolvedPoints] = await Promise.all([
        Promise.all(availableRooms.map(resolveLatest)),
        fetchRcn(getRcnHistoryQuery(address.rcnAddress, defaultRooms))
    ]);
    const defaultPoint = resolvedPoints[availableRooms.indexOf(defaultRooms)];

    if (!defaultPoint) {
        return true;
    }

    // The comparison chart, the rooms section and the FAQ read the district and the city for the period of
    // the building's point, in its grain - the prefetch has to follow both, otherwise the browser would refetch.
    const {point, grain} = defaultPoint;
    const comparisonRegions = [address.district, address.city].filter((region): region is IPricesAddressRegion => region !== null);

    await Promise.all([
        fetchRcn(getRcnPeriodQuery(address.rcnAddress, defaultRooms, "total", point.period_start, grain)),
        ...comparisonRegions.map((region) => fetchRcn(getRcnPeriodQuery(region.id, defaultRooms, "per_m2", point.period_start, grain)))
    ]);

    return true;
};
import {IFetchContext} from "@pg-mono/data-fetcher";
import {createRequestActionTypes} from "@pg-mono/request-state";
import {enable302ResponseState, enableErrorResponseState} from "@pg-mono/response-state";

import {IRPStore} from "../../../app/rp_reducer";
import {IRPRequestMeta} from "../../../app/rp_request_meta";
import {IAppDispatch} from "../../../store/store";
import {getRcnDataApi} from "../api/get_rcn_data_api";
import {getDefaultRoomsFilter, PRICES_ADDRESS_ROOMS} from "../constants/prices_address_rooms";
import {ALL_ROOMS, RoomsFilter} from "../constants/rcn_rooms_filter";
import {IPricesAddressRegion} from "../types/IPricesAddress";
import {RcnPriceStatsQuery} from "../types/RcnPriceStatsQuery";
import {RcnPriceStatsResponse} from "../types/RcnPriceStatsResponse";
import {getPublishableRcnPoint} from "../utils/get_publishable_rcn_point";
import {getRcnHistoryQuery, getRcnPeriodQuery} from "../utils/get_rcn_params";
import {fetchRcnLatestPoint, getRcnLatestYearQuery, IRcnResolvedPoint} from "../utils/resolve_rcn_latest_point";
import {getPricesAddressUnavailableUrl} from "./fetch_prices_address";

const FETCH_PRICES_ADDRESS_ROOMS = "reports/FETCH_PRICES_ADDRESS_ROOMS";
export const fetchPricesAddressRoomsTypes = createRequestActionTypes(FETCH_PRICES_ADDRESS_ROOMS);

const RCN_UNAVAILABLE_STATUS = 500;

// The aggregate first, then one probe per room count. The aggregate decides whether the page exists at
// all - it covers rooms 1-10 against the registry's three-transaction floor, so it publishes for addresses
// where no single size does. The per-size probes only fill the selector, and they double as a fallback:
// one flaky request must not turn an address that has data into a 500.
const PROBE_FILTERS: readonly RoomsFilter[] = [ALL_ROOMS, ...PRICES_ADDRESS_ROOMS];

interface IRcnResult {
    isSuccess: boolean;
    response: RcnPriceStatsResponse | null;
}

/**
 * 1. One probe per rooms filter: its newest YEAR point. A month or a quarter with three sales means its year has
 *    them too, so the year alone answers whether the filter publishes anything at all, however long ago.
 * 2. For every filter that does, the newest month and quarter in parallel - the year is the probe, a cache hit - and
 *    the latest period wins (fetchRcnLatestPoint, the rule the page hooks apply to the same three queries), so the
 *    headline, the rooms tabs and the spread hydrate without a request. History alongside: the year series (the
 *    grain useRcnHistory opens on) - the query useRcnHistory makes.
 * 3. For the opening filter: "total" and the district / city for the resolved period, in the resolved grain.
 *
 * No query depends on "today", and market is always omitted (both markets combined, one request per read - see
 * get_rcn_params), so the browser builds the same cache keys whenever the (cached) page is hydrated.
 */
export const fetchPricesAddressRcnData = (_ctx: IFetchContext<IRPRequestMeta>) => async (dispatch: IAppDispatch, getState: () => IRPStore) => {
    const address = getState().report.price.address;

    if (!address) {
        return false;
    }

    dispatch({type: fetchPricesAddressRoomsTypes.start});

    const fetchRcnResponse = (query: RcnPriceStatsQuery): Promise<RcnPriceStatsResponse> =>
        dispatch(getRcnDataApi.endpoints.getRcnData.initiate(query)).unwrap();
    const fetchRcn = (query: RcnPriceStatsQuery): Promise<IRcnResult> =>
        fetchRcnResponse(query)
            .then((response) => ({isSuccess: true, response}))
            .catch(() => ({isSuccess: false, response: null}));

    const probeResults = await Promise.all(PROBE_FILTERS.map((rooms) => fetchRcn(getRcnLatestYearQuery(address.rcnAddress, rooms))));

    // An empty series means the registry has no group of three transactions, while a rejected request
    // means the registry could not be reached - only the former is an answer about this address.
    if (probeResults.every((result) => !result.isSuccess)) {
        dispatch(enableErrorResponseState(RCN_UNAVAILABLE_STATUS));

        return false;
    }

    const availableRooms = PROBE_FILTERS.filter((_rooms, index) => getPublishableRcnPoint(probeResults[index].response) !== null);

    if (availableRooms.length === 0) {
        // "No data" is only an answer when every probe gave one: a failed probe - above all the aggregate's, which
        // publishes for buildings where no single size does - leaves the address unknown, not unavailable.
        if (probeResults.some((result) => !result.isSuccess)) {
            dispatch(enableErrorResponseState(RCN_UNAVAILABLE_STATUS));

            return false;
        }

        dispatch(enable302ResponseState(getPricesAddressUnavailableUrl()));

        return false;
    }

    dispatch({type: fetchPricesAddressRoomsTypes.success, result: availableRooms});

    const defaultRooms = getDefaultRoomsFilter(availableRooms);
    // A failed month or quarter leaves the newest point of the grains that answered (failedGrains) - the page shows
    // it, and the hooks retry the failed grain in the browser. The year itself cannot fail here: it is the probe.
    const resolveLatest = (rooms: RoomsFilter): Promise<IRcnResolvedPoint | null> =>
        fetchRcnLatestPoint(fetchRcnResponse, address.rcnAddress, rooms).then(
            ({resolved}) => resolved,
            () => null
        );

    const [resolvedPoints] = await Promise.all([
        Promise.all(availableRooms.map(resolveLatest)),
        fetchRcn(getRcnHistoryQuery(address.rcnAddress, defaultRooms))
    ]);
    const defaultPoint = resolvedPoints[availableRooms.indexOf(defaultRooms)];

    if (!defaultPoint) {
        return true;
    }

    // The comparison chart, the rooms section and the FAQ read the district and the city for the period of
    // the building's point, in its grain - the prefetch has to follow both, otherwise the browser would refetch.
    const {point, grain} = defaultPoint;
    const comparisonRegions = [address.district, address.city].filter((region): region is IPricesAddressRegion => region !== null);

    await Promise.all([
        fetchRcn(getRcnPeriodQuery(address.rcnAddress, defaultRooms, "total", point.period_start, grain)),
        ...comparisonRegions.map((region) => fetchRcn(getRcnPeriodQuery(region.id, defaultRooms, "per_m2", point.period_start, grain)))
    ]);

    return true;
};
